import { useQuery } from '@tanstack/react-query';
import {
  AccessorKeyColumnDef,
  Cell,
  createColumnHelper,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  Row as RowType,
  useReactTable
} from '@tanstack/react-table';
import FeatherIcon from 'feather-icons-react';
import { RETURN_ORDER_STATUS } from 'khufu-legos';
import { useEffect, useState } from 'react';
import { Badge, Card, Col, Container, Form, InputGroup, Row, Table } from 'react-bootstrap';
import { Link, Route, Routes } from 'react-router-dom';

import { getReturnOrders } from '../../apis/queries';
import EmptyTableState from '../../components/EmptyState/EmptyStateTable';
import Loader from '../../components/Loader';
import PaginationControls from '../../components/Pagination/PaginationControls';
import { currencyFormatter } from '../../helpers/currencyFormatter';
import getStatusColor from '../../helpers/getStatusColor';
import { ReturnOrder } from '../../types';
// import { formatDateTime } from '../../utils/getTimeFormat';
import { formatDateShort } from '../../utils/formatShortDate';
import { formatTime } from '../../utils/formatTime';
import AnalyticsHeader from '../../widgets/Header';

interface ReturnOrdersTableProps {
  query?: Record<'status', RETURN_ORDER_STATUS>;
}

export const RETURN_ORDERS_QUERY_KEY = ['returnOrders'];

const globalFilterFn: FilterFn<ReturnOrder> = (row, columnId, filterValue) => {
  const returnOrderNumber = row.original.returnOrderNumber?.toString().toLowerCase();
  const sourceOrderNumber = row.original.sourceOrderNumber?.toString().toLowerCase();
  const customerName = row.original.customer?.name?.toLowerCase() ?? '';
  const createdBy = row.original.createdBy?.fullName?.toLowerCase() ?? '';
  const searchValue = filterValue.toLowerCase();

  return (
    returnOrderNumber.includes(searchValue) ||
    sourceOrderNumber.includes(searchValue) ||
    customerName.includes(searchValue) ||
    createdBy.includes(searchValue)
  );
};

const columnHelper = createColumnHelper<ReturnOrder>();

const columns = [
  columnHelper.accessor('returnOrderNumber', {
    header: 'Return Order Number',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('sourceOrderNumber', {
    header: 'Source Order',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('customer.name', {
    header: 'Customer',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: info => <Badge bg={`${getStatusColor(info.getValue())}-soft`}>{info.getValue()}</Badge>,
    footer: info => info.column.id
  }),
  columnHelper.accessor('type', {
    header: 'Order Type',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('reason', {
    header: 'Reason',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('total', {
    header: 'Total',
    cell: info => currencyFormatter(info.getValue()),
    footer: info => info.column.id
  }),
  columnHelper.accessor('productsCases', {
    header: 'Cases',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created At',
    cell: info => formatDateShort(info.getValue()),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created Time',
    id: 'createdTime',
    cell: info => formatTime(info.getValue()),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('createdBy.fullName', {
    header: 'Created By',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  })
];

const limit = 100;

const ReturnOrdersTable = ({ query }: ReturnOrdersTableProps) => {
  const [page, setPage] = useState(1);
  const compositeQueryKey = query
    ? [...RETURN_ORDERS_QUERY_KEY, { ...query, page, limit }]
    : [RETURN_ORDERS_QUERY_KEY, { page, limit }];
  const { data, isLoading } = useQuery({
    queryKey: compositeQueryKey,
    queryFn: () => getReturnOrders({ ...query, page, limit })
  });
  const returnOrders = data?.results ?? [];

  const { count } = data ?? {};

  const nextPage = data?.nextPage;
  const prevPage = data?.prevPage;

  useEffect(() => {
    setPage(1);
  }, [query]);

  let noOrdersMessage;
  switch (query?.status) {
    case 'open':
      noOrdersMessage = returnOrders && returnOrders.length === 0 && <EmptyTableState message="No open orders yet." />;
      break;
    case 'cancelled':
      noOrdersMessage = returnOrders && returnOrders.length === 0 && (
        <EmptyTableState message="No cancelled orders yet." />
      );
      break;
    case 'fulfilled':
      noOrdersMessage = returnOrders && returnOrders.length === 0 && (
        <EmptyTableState message="No fulfilled orders yet." />
      );
      break;
    case 'restocked':
      noOrdersMessage = returnOrders && returnOrders.length === 0 && (
        <EmptyTableState message="No restocked orders yet." />
      );
      break;
    default:
      noOrdersMessage = null;
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <>
      <AnalyticsHeader title="Return Orders" tabs={tabRoutes} />
      <div className="main-content">
        <Container fluid>
          <Row className="justify-content-center">
            <Col xs={12}>
              {isLoading ? (
                <Loader />
              ) : noOrdersMessage ? (
                noOrdersMessage
              ) : returnOrders && returnOrders.length > 0 ? (
                <>
                  <TableList returnOrders={returnOrders} columns={columns} />
                  <PaginationControls
                    currentPage={page}
                    totalPages={count ?? 0}
                    onPageChange={handlePageChange}
                    nextPage={nextPage ?? null}
                    prevPage={prevPage ?? null}
                  />
                </>
              ) : (
                <EmptyTableState message="No orders returned yet." />
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

const tabRoutes = [
  {
    title: 'All',
    path: 'all',
    tabLink: '/return-orders/all',
    element: <ReturnOrdersTable />
  },
  {
    title: 'Open',
    path: 'open',
    tabLink: '/return-orders/open',
    element: <ReturnOrdersTable query={{ status: RETURN_ORDER_STATUS.OPEN }} />
  },
  {
    title: 'Fulfilled',
    path: 'fulfilled',
    tabLink: '/return-orders/fulfilled',
    element: <ReturnOrdersTable query={{ status: RETURN_ORDER_STATUS.FULFILLED }} />
  },
  {
    title: 'Restocked',
    path: 'restocked',
    tabLink: '/return-orders/restocked',
    element: <ReturnOrdersTable query={{ status: RETURN_ORDER_STATUS.RESTOCKED }} />
  },
  {
    title: 'Cancelled',
    path: 'cancelled',
    tabLink: '/return-orders/cancelled',
    element: <ReturnOrdersTable query={{ status: RETURN_ORDER_STATUS.CANCELED }} />
  }
];

const ReturnOrders = () => {
  return (
    <>
      <div className="main-content">
        <Routes>
          {tabRoutes.map(({ path, element, title }) => (
            <Route key={title} path={path} element={element} />
          ))}
        </Routes>
      </div>
    </>
  );
};

export default ReturnOrders;

interface TableListProps {
  returnOrders: ReturnOrder[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: AccessorKeyColumnDef<ReturnOrder, any>[];
}

const TableList = ({ returnOrders, columns }: TableListProps) => {
  const [globalFilter, setGlobalFilter] = useState<string | undefined>(undefined);

  const tableInstance = useReactTable({
    data: returnOrders!,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: { globalFilter },
    globalFilterFn,
    onGlobalFilterChange: setGlobalFilter
  });

  return (
    <Card>
      <Card.Header>
        <InputGroup className="input-group-merge input-group-flush input-group-reverse">
          <Form.Control
            type="search"
            placeholder="Search"
            onChange={e => setGlobalFilter(e.target.value ? e.target.value : undefined)}
          />
          <InputGroup.Text>
            <FeatherIcon icon="search" size="1em" />
          </InputGroup.Text>
        </InputGroup>
      </Card.Header>

      <Table size="sm" className="card-table table-nowrap" hover responsive>
        <thead>
          {tableInstance.getHeaderGroups().map(headerGroup => {
            return (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th colSpan={header.colSpan} key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody>
          {tableInstance.getRowModel().rows.map(row => {
            return (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => {
                  return <td key={cell.id}>{renderCell(row, cell)}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
};

const renderCell = (row: RowType<ReturnOrder>, cell: Cell<ReturnOrder, unknown>) => {
  switch (cell.column.id) {
    case 'returnOrderNumber':
      return (
        <Link to={`/return-order/${row.original._id}`}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </Link>
      );
    case 'sourceOrderNumber':
      return (
        <Link to={`/order/${row.original.sourceOrderId}`}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </Link>
      );
    default:
      return flexRender(cell.column.columnDef.cell, cell.getContext());
  }
};
